import React, { useState, useRef, useEffect } from "react";
import "../../../styles/Part4.css";

const TalkQuestion = ({
  talk,
  onComplete,
  onNext,
  onBack,
  onPrevious,
  hasNext,
  studyMode = "both",
  accentType = "us",
  currentIndex,
  totalCount,
}) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showScript, setShowScript] = useState(false);
  const [showExplanations, setShowExplanations] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showAnswers, setShowAnswers] = useState(false);
  const [version, setVersion] = useState("basic");
  const [currentAccentType, setCurrentAccentType] = useState(accentType);
  const audioRef = useRef(null);
  const [hideUnmarkedOptions, setHideUnmarkedOptions] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const speedOptions = [1, 1.5, 2];
  const [viewMode, setViewMode] = useState("pure"); // 'all', 'marked', 'pure'

  useEffect(() => {
    setSelectedAnswers({});
    setShowAnswers(false);
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
    }
  }, [talk]);

  useEffect(() => {
    // 컴포넌트 마운트 시 audio 엘리먼트 참조
    audioRef.current = document.querySelector("audio");

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const handleAnswerSelect = (questionId, answer) => {
    const newSelectedAnswers = {
      ...selectedAnswers,
      [questionId]: answer,
    };
    setSelectedAnswers(newSelectedAnswers);
  };

  const handleSpeedChange = (speed) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
      setPlaybackRate(speed);
    }
  };

  const handleAudioPlay = () => {
    if (!isPlaying) {
      const audioPath =
        "/" + talk.versions[version].audioUrl[currentAccentType];
      console.log("Playing audio:", audioPath);

      if (audioRef.current) {
        audioRef.current.src = audioPath;
        audioRef.current.playbackRate = playbackRate;
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
          })
          .catch((error) => {
            console.error("오디오 재생 실패:", error);
          });
      }
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const renderHTML = (text) => {
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
  };

  const renderOptionText = (text) => {
    if (viewMode === "all") {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    } else if (viewMode === "marked") {
      const matches = text.match(/<mark>.*?<\/mark>/g);
      if (!matches) return "";
      return <span dangerouslySetInnerHTML={{ __html: matches.join(" ") }} />;
    } else if (viewMode === "pure") {
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: text.replace(/<\/?mark>/g, ""),
          }}
        />
      );
    }
  };

  const checkAllAnswersSelected = (answers) => {
    return Object.keys(answers).length === 3;
  };

  return (
    <div>
      <div className="talk-header">
        <button className="back-button" onClick={onBack}>
          ← 카테고리로 돌아가기
        </button>
        <div className="talk-info">
          <span className="talk-number">
            지문 {currentIndex + 1}/{totalCount}
          </span>
          <h3>
            {talk.title}
            <span className="title-kr">{talk.titleKr}</span>
          </h3>
        </div>
        <div className="control-buttons">
          <button
            className={`control-btn play-btn ${isPlaying ? "playing" : ""}`}
            onClick={handleAudioPlay}
          >
            <img
              src={`/icons/${isPlaying ? "stop" : "play"}.svg`}
              alt={isPlaying ? "정지" : "재생"}
              className="play-icon"
            />
            <span className="play-text">{isPlaying ? "정지" : "재생"}</span>
          </button>

          <div className="speed-buttons">
            {speedOptions.map((speed) => (
              <button
                key={speed}
                className={`control-btn speed-btn ${
                  playbackRate === speed ? "active" : ""
                }`}
                onClick={() => handleSpeedChange(speed)}
              >
                x{speed}
              </button>
            ))}
          </div>

          <button
            className={`control-btn accent-btn ${
              currentAccentType === "us" ? "active" : ""
            }`}
            onClick={() =>
              setCurrentAccentType(currentAccentType === "us" ? "uk" : "us")
            }
          >
            {currentAccentType === "us" ? "미국" : "영국"}
          </button>

          <div className="view-mode-buttons">
            <button
              className={`control-btn view-mode-btn ${
                viewMode === "pure" ? "active" : ""
              }`}
              onClick={() => setViewMode("pure")}
            >
              순수
            </button>
            <button
              className={`control-btn view-mode-btn ${
                viewMode === "all" ? "active" : ""
              }`}
              onClick={() => setViewMode("all")}
            >
              전체
            </button>
            <button
              className={`control-btn view-mode-btn ${
                viewMode === "marked" ? "active" : ""
              }`}
              onClick={() => setViewMode("marked")}
            >
              마크
            </button>
          </div>
        </div>
      </div>

      <div className="talk-question">
        <div className="questions-section">
          {talk.questions.map((question) => (
            <div key={question.id} className="question">
              <h4>
                {renderHTML(question.question)}
                {showTranslation && (
                  <span className="question-kr">{question.questionKr}</span>
                )}
              </h4>
              <div className="options">
                {Object.entries(question.options).map(([key, value]) => (
                  <label key={key} className="option-label">
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={key}
                      checked={selectedAnswers[question.id] === key}
                      onChange={() => handleAnswerSelect(question.id, key)}
                    />
                    <div className="option-content">
                      {key}. {renderOptionText(value)}
                      {showTranslation && (
                        <span className="option-kr">
                          {question.optionsKr[key]}
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
              {showAnswers && (
                <div
                  key={`answer-${question.id}`}
                  className="answer-explanation"
                >
                  <p
                    className={
                      selectedAnswers[question.id] === question.correctAnswer
                        ? "correct-answer"
                        : "wrong-answer"
                    }
                  >
                    {selectedAnswers[question.id] === question.correctAnswer
                      ? `정답: ${question.correctAnswer}`
                      : `오답입니다. 정답은 ${question.correctAnswer}입니다.`}
                  </p>
                  <div className="explanation">
                    {(question.explanation
                      ? question.explanation.split("[선택지 해석]")
                      : []
                    ).map((section, index) => {
                      if (index === 0 || !section) {
                        return null;
                      } else {
                        const parts = section.split("\n\n정답") || [];
                        const translations = parts[0] || "";
                        const explanation = parts[1] || "";
                        return (
                          <div key={index}>
                            <div className="translations">
                              {translations.split("\n").map((line, i) => (
                                <p key={i}>{line.trim()}</p>
                              ))}
                            </div>
                            <p className="explanation-text">{explanation}</p>
                          </div>
                        );
                      }
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
          {/* 모든 답을 선택했고 아직 답을 보여주지 않았을 때만 답 확인하기 버튼 표시 */}
          {Object.keys(selectedAnswers).length === talk.questions.length &&
            !showAnswers && (
              <button
                className="show-answer-btn"
                onClick={() => setShowAnswers(true)}
              >
                답 확인하기
              </button>
            )}
          <div className="button-container">
            <button
              className="nav-button prev"
              onClick={onPrevious}
              disabled={currentIndex === 0}
            >
              이전
            </button>

            {hasNext ? (
              <button className="nav-button next" onClick={onNext}>
                다음
              </button>
            ) : (
              <button
                className="complete-button"
                onClick={() => {
                  console.log("완료 버튼 클릭됨");
                  if (typeof onComplete === "function") {
                    console.log("onComplete 함수 실행");
                    onComplete();
                  } else {
                    console.log("onComplete is not a function:", onComplete);
                  }
                }}
              >
                학습 완료
              </button>
            )}
          </div>
        </div>

        <div className="talk-script">
          <button
            className="toggle-script"
            onClick={() => setShowScript(!showScript)}
          >
            스크립트 {showScript ? "숨기기" : "보기"}
          </button>
          {showScript && (
            <div className="script-content">
              <div className="english-script">
                <pre>{renderHTML(talk.versions[version].script)}</pre>
              </div>
              <div className="korean-translation">
                <pre>{talk.versions[version].translation}</pre>
              </div>
            </div>
          )}
        </div>
      </div>

      <audio
        ref={audioRef}
        onEnded={() => setIsPlaying(false)}
        onError={(e) => console.error("오디오 에러:", e)}
      />
    </div>
  );
};

export default TalkQuestion;
